import { h } from 'preact';
import { useState } from 'preact/hooks';
import { withCartContext } from './cart-context';
import CartService from './cart-service';

const PROMO_CODE_STORAGE_KEY = 'cartPromoCode';

export const getStoredPromoCode = () => {
  try {
    return localStorage.getItem(PROMO_CODE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

export const clearStoredPromoCode = () => {
  try {
    localStorage.removeItem(PROMO_CODE_STORAGE_KEY);
  } catch {
    // silent
  }
};

export const PROMO_REMOVED_FLAG = 'promoJustRemoved';

/**
 * Extracts the active discount code from the Shopify AJAX cart state.
 * This is the source of truth — it reflects whatever discount is active
 * in the Shopify session, whether applied from the cart UI or checkout.
 */
export const getCartDiscountCode = (cartState) => {
  // Only consider a discount code active when total_discount > 0.
  // Shopify's AJAX cart can retain a stale code in cart_level_discount_applications
  // even after it's been removed in checkout; total_discount is the reliable indicator.
  if (!cartState?.total_discount || cartState.total_discount <= 0) return '';

  // Check cart-level discount applications first (order-wide discount codes)
  const apps = cartState?.cart_level_discount_applications || [];
  const cartLevelDiscount = apps.find((d) => d.type === 'discount_code');
  if (cartLevelDiscount?.title) return cartLevelDiscount.title;

  // Fallback: check line-level discount allocations on each item.
  // Product-specific discount codes only appear at the line level.
  const items = cartState?.items || [];
  for (const item of items) {
    const lineDiscounts = item.line_level_discount_allocations || [];
    for (const alloc of lineDiscounts) {
      const da = alloc.discount_application;
      if (da && da.type === 'discount_code' && da.title) {
        return da.title;
      }
    }
  }

  return '';
};

/**
 * Applies a discount code via the AJAX Cart API only.
 * Does NOT hit /discount/{code} to avoid setting a persistent session cookie.
 * The session discount is set later at checkout navigation time via getCheckoutUrl().
 */
const applyDiscountToSession = async (code) => {
  const res = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: code }),
  });
  return res.json();
};

/**
 * Clears any active discount code from the Shopify session.
 * Posts discount: '' to the AJAX Cart API, then refreshes the cart.
 */
const clearDiscountFromSession = async () => {
  // Primary method: POST empty discount to AJAX Cart API
  await fetch(`${window.Shopify.routes.root}cart/update.js`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ discount: '' }),
  });

  // Verify & fallback: if the AJAX clear didn't work, navigate to /discount/ to clear session
  const checkRes = await fetch('/cart.js');
  const cart = await checkRes.json();
  const stillHasDiscount = (cart.cart_level_discount_applications || []).some((d) => d.type === 'discount_code');

  if (stillHasDiscount) {
    await fetch('/discount/', { redirect: 'follow' });
  }

  return CartService.get();
};

const PromoCode = ({ cart }) => {
  // Derive applied code from actual cart state, not just localStorage
  const appliedCode = getCartDiscountCode(cart.state);

  // Keep localStorage in sync with the cart state so getCheckoutUrl() works
  if (appliedCode && getStoredPromoCode() !== appliedCode) {
    try {
      localStorage.setItem(PROMO_CODE_STORAGE_KEY, appliedCode);
    } catch {
      /* silent */
    }
  } else if (!appliedCode && getStoredPromoCode()) {
    clearStoredPromoCode();
  }

  const [isOpen, setIsOpen] = useState(!!appliedCode);
  const [code, setCode] = useState(appliedCode || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleApply = async (e) => {
    e.preventDefault();
    const trimmed = code.trim();
    if (!trimmed) return;

    setLoading(true);
    setError('');

    try {
      const updatedCart = await applyDiscountToSession(trimmed);

      // Check whether Shopify actually applied the discount
      const hasDiscount =
        (updatedCart.total_discount && updatedCart.total_discount > 0) ||
        (updatedCart.cart_level_discount_applications && updatedCart.cart_level_discount_applications.length > 0);

      if (hasDiscount) {
        try {
          localStorage.setItem(PROMO_CODE_STORAGE_KEY, trimmed);
        } catch {
          /* silent */
        }
        // Refresh cart via CartService so the Cart component re-renders
        // with the updated localStorage value already in place
        CartService.get();
      } else {
        setError(window.TMW.Strings.cart.promoCodeInvalid || 'This code is not valid');
      }
    } catch {
      setError(window.TMW.Strings.cart.promoCodeError || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async () => {
    setLoading(true);
    setError('');

    // Clear local state first so any Cart re-render sees the updated localStorage
    clearStoredPromoCode();
    setCode('');
    setIsOpen(false);
    sessionStorage.setItem(PROMO_REMOVED_FLAG, '1');

    try {
      await clearDiscountFromSession();
    } catch {
      // Proceed even if the fetch fails
    }

    setLoading(false);
  };

  return (
    <div className="mb-12">
      {!appliedCode && (
        <button
          type="button"
          className="body-14 flex cursor-pointer items-center gap-4 border-none bg-transparent p-0 underline"
          onClick={() => setIsOpen(!isOpen)}
          disabled={loading}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="#FACC15"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
            focusable="false"
          >
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
            <circle
              cx="7"
              cy="7"
              r="1.5"
              fill="white"
              stroke="currentColor"
            />
          </svg>
          {window.TMW.Strings.cart.promoCodeToggle || 'Add promo code'}
        </button>
      )}
      {isOpen && !appliedCode && (
        <form
          onSubmit={handleApply}
          className="mt-8 flex gap-8"
        >
          <input
            type="text"
            value={code}
            onInput={(e) => {
              setCode(e.target.value);
              if (error) setError('');
            }}
            placeholder={window.TMW.Strings.cart.promoCodePlaceholder || 'Enter code'}
            className={`body-14 h-40 flex-1 rounded border px-12 ${error ? 'border-error' : 'border-grey-4'}`}
            disabled={loading}
          />
          <button
            type="submit"
            className="cta-blue h-40 min-w-0 rounded px-16"
            disabled={!code.trim() || loading}
          >
            {loading ? '...' : window.TMW.Strings.cart.promoCodeApply || 'Apply'}
          </button>
        </form>
      )}
      {error && <p className="body-12 mt-4 text-error">{error}</p>}
      {appliedCode && (
        <div className="flex items-center justify-between">
          <span className="body-14">
            {window.TMW.Strings.cart.promoCodeLabel || 'Promo code'}: <strong>{appliedCode}</strong>
          </span>
          <button
            type="button"
            className="body-12 cursor-pointer border-none bg-transparent p-0 underline"
            onClick={handleRemove}
            disabled={loading}
          >
            {window.TMW.Strings.cart.promoCodeRemove || 'Remove'}
          </button>
        </div>
      )}
    </div>
  );
};

export default withCartContext(PromoCode);
